import React from 'react';
import css from './MediaDetail.module.scss';
import { openForEditing } from './MediaMargin';
import type { MediaLabel } from '../viewModel/mediaLabels';

/** The control that puts the panel away again. */
export const MEDIA_DETAIL_CLOSE = 'Close this list';

/** What the panel is called, said as the fact it is about. */
export function mediaDetailHeading(line: number): string {
  return `Everything attached to line ${line}`;
}

export type MediaDetailProps = {
  /** The 1-based script line the writer asked about. */
  line: number;
  /** Every label that line carries, in the order the margin would have shown them. */
  labels: MediaLabel[];
  /** The protected exit into the scene's own editor. The surface asks the writer first. */
  onOpenScene?: (branchId: number) => void;
  /** Put the panel away. Nothing else changes. */
  onDismiss: () => void;
};

/**
 * T19-04: one line's labels, in full, in a space of its own.
 *
 * The margin is one row per script line and every row is exactly as tall as its line, so whatever a
 * row holds has to fit on that line or it hangs down over the next one. A scene heading can carry a
 * location, a picture and a piece of music, with a protected exit beside them, and a missing-name
 * marker on top of that; no width of margin puts all of it on one line.
 *
 * So the margin keeps what fits and says how much more there is, and "more" opens this. It is built
 * the way `NameNotes` is, and for the same reason: a block in the ordinary flow above the
 * workspace, taking the height its own content needs, covering nothing. The scene's exit comes with
 * the labels, because the writer who opened the list is the writer who wanted to act on it.
 */
export function MediaDetail(props: MediaDetailProps) {
  const {
    line, labels, onOpenScene, onDismiss,
  } = props;

  if (labels.length === 0) {
    return null;
  }
  const { sceneName, branchId } = labels[0];

  return (
    <div className={css.panel} data-testid="basil-media-detail" data-line={line}>
      <div className={css.top}>
        <h2 className={css.heading}>{mediaDetailHeading(line)}</h2>
        <button type="button" className={css.dismiss} onClick={onDismiss}>{MEDIA_DETAIL_CLOSE}</button>
      </div>
      <ul className={css.list}>
        {labels.map((label) => (
          <li className={css.entry} data-kind={label.kind} key={`${label.kind}-${label.text}`}>
            {label.text}
          </li>
        ))}
      </ul>
      {branchId != null && onOpenScene ? (
        <button
          type="button"
          className={css.open}
          onClick={() => onOpenScene(Number(branchId))}
        >
          {openForEditing(sceneName)}
        </button>
      ) : null}
    </div>
  );
}
